import React, { useMemo, useRef } from "react"
import cn from "../../../utils/classes"
import RowContainer from "./Layout/RowContainer"
import ColumnContainer from "./Layout/ColumnContainer"
import SkeletonLoader from "./SkeletonLoader"
import { ButtonWrapper } from "./ButtonWrapper"
import Spinner from "./Spinner"
import { Typography, typographyTypes } from "./Typography"
import { Label } from "./Label"
import Icon from "./Icon"

export interface FileDownloadProps {
  text: {
    linkText: string
    submittedText?: string
    errorText?: string
    removeText?: string
    resetText?: string
    ariaText?: string
  }
  onClick: () => void
  iconStyleOverrides?: string
  loadingStyleOverrides?: string
  rowStyleOverrides?: string
  linkStyleOverrides?: string
  labelStyleOverrides?: string
  error?: boolean
  loading?: boolean
  showSkeleton?: boolean
  onRemove?: () => void
  onReset?: () => void
}

/**
 * `FileDownload` is a component that displays a file download link with an icon and a label.
 *
 * @component
 * @param {object} props - The properties that define the FileDownload component.
 * @param {object} props.text - The text for the link and the label.
 * @param {string} props.text.linkText - The text for the link.
 * @param {string} props.text.submittedText - The text for the label.
 * @param {string} props.text.errorText - The text for the error label.
 * @param {function} props.onClick - Callback to be executed when the download button is click.
 * @param {string} [props.iconStyleOverrides] - Override styles for the icon.
 * @param {string} [props.loadingStyleOverrides] - Override styles for the loading spinner.
 * @param {string} [props.rowStyleOverrides] - Override styles for the row container.
 * @param {string} [props.linkStyleOverrides] - Override styles for the link.
 * @param {string} [props.labelStyleOverrides] - Override styles for the label.
 * @param {string} [props.error=false] - Indicates if there is an error.
 * @param {string} [props.loading=false] - Indicates if download is underway.
 * @param {string} [props.showSkeleton=false] - Indicates the skeleton loader is shown in place of the file download.
 * @param {function} props.onClick - Callback to be executed when the button to remove the file is clicked. If this is undefined, the button will not be displayed.
 * @param {function} [props.onReset] - Callback for the "Re-sign and Replace" link (US 20047). Rendered only when `text.resetText` is also supplied.
 * @returns {React.Component} The FileDownload component.
 */
export const FileDownload: React.FC<FileDownloadProps> = ({
  text,
  onClick,
  iconStyleOverrides,
  loadingStyleOverrides,
  rowStyleOverrides,
  linkStyleOverrides,
  labelStyleOverrides,
  error = false,
  loading = false,
  showSkeleton = false,
  onRemove,
  onReset,
}) => {
  const defaultIconStyles = "p-0 m-0"
  const defaultLoadingStyles = "pt-2 pr-3 h-full"
  const defaultRowStyles = "space-x-0 p-0"
  const defaultLinkStyles = "font-semibold text-sm text-primary-blue"
  const defaultLabelStyles = "font-medium"

  const iconClassName = useMemo(
    () => cn(defaultIconStyles, iconStyleOverrides),
    [defaultIconStyles, iconStyleOverrides],
  )
  const loadingClassName = useMemo(
    () => cn(defaultLoadingStyles, loadingStyleOverrides),
    [defaultLoadingStyles, loadingStyleOverrides],
  )
  const rowClassName = useMemo(
    () => cn(defaultRowStyles, rowStyleOverrides),
    [defaultRowStyles, rowStyleOverrides],
  )
  const linkClassName = useMemo(
    () => cn(defaultLinkStyles, linkStyleOverrides),
    [defaultLinkStyles, linkStyleOverrides],
  )
  const labelClassName = useMemo(
    () => cn(defaultLabelStyles, labelStyleOverrides),
    [defaultLabelStyles, labelStyleOverrides],
  )

  const linkRef = useRef<HTMLAnchorElement>(null)

  const handleClick = () => {
    linkRef.current?.click()
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault()
      linkRef.current?.click()
    }
  }

  return showSkeleton ? (
    <RowContainer styleOverrides="space-x-3 w-full">
      <ColumnContainer>
        <SkeletonLoader width="40px" height="40px" borderRadius="full" />
      </ColumnContainer>
      <ColumnContainer styleOverrides="space-y-6 pt-2">
        <SkeletonLoader width="250px" height="16px" count={2} />
      </ColumnContainer>
    </RowContainer>
  ) : (
    <ColumnContainer styleOverrides="space-y-2">
      <RowContainer styleOverrides={rowClassName}>
        <ButtonWrapper
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          styleOverrides="p-0 h-fit mr-[16.5px]"
          shouldWrapChildren={false}
        >
          {loading ? (
            <Spinner
              size="xs"
              parentStyleOverrides={loadingClassName}
              text={""}
            />
          ) : (
            <Icon iconType={"fileDownload"} styleOverrides={iconClassName} />
          )}
        </ButtonWrapper>
        <ColumnContainer styleOverrides="space-y-3 pt-3">
          <ButtonWrapper
            onClick={onClick}
            justification="start"
            buttonWrapperStyleType="table"
          >
            <Typography
              variant={typographyTypes.HREF}
              text={text.linkText}
              styleOverrides={linkClassName}
            />
          </ButtonWrapper>
          <RowContainer styleOverrides="space-x-2">
            {text.submittedText && (
              <Label
                text={text.submittedText}
                htmlFor={text.submittedText}
                size="sm"
                styleOverrides={labelClassName}
              />
            )}
            <div>
              {text.removeText && onRemove && (
                <ButtonWrapper
                  onClick={onRemove}
                  ariaLabel={`${text.removeText} ${text.ariaText}`}
                  justification="start"
                  buttonWrapperStyleType="table"
                  //if both remove and reset buttons are present remove the min height setting to prevent click boundary overlap
                  {...(text.resetText && {
                    styleOverrides: "mb-2 after:min-h-0",
                  })}
                >
                  <Typography
                    variant={typographyTypes.HREF}
                    text={text.removeText}
                    styleOverrides={linkClassName}
                  />
                </ButtonWrapper>
              )}
              {text.resetText && (
                <ButtonWrapper
                  onClick={onReset ?? (() => {})}
                  disabled={!onReset}
                  ariaLabel={`${text.resetText} ${text.ariaText}`}
                  justification="start"
                  buttonWrapperStyleType="table"
                  //if both remove and reset buttons are present remove the min height setting to prevent click boundary overlap
                  styleOverrides={cn(
                    !onReset && "invisible",
                    text.removeText && onRemove && "after:min-h-0",
                  )}
                >
                  <Typography
                    variant={typographyTypes.HREF}
                    text={text.resetText}
                    styleOverrides={linkClassName}
                  />
                </ButtonWrapper>
              )}
            </div>
          </RowContainer>
        </ColumnContainer>
      </RowContainer>
      {error && text.errorText && (
        <Typography variant={typographyTypes.ERROR} text={text.errorText} />
      )}
    </ColumnContainer>
  )
}
